import * as React from "react";
import {
  Column,
  Columns,
  Divider,
  Stack,
  Text,
} from "@origin-digital/ods-core";
import { useFormikContext } from "formik";
import { AccountPayer, Contact, FormValues, GasOptionEnum } from "./types";
import ReadOnlyFieldWrapper from "./ReadOnlyFieldWrapper";
import { mapGasOptionToLabel } from "./FormComponents/GasSectionInPremise";
import { apiClient } from "src/api/apiService";
import { AppContext } from "src/context/AppContext";

const ReadOnlyGasSectionInPremise: React.FC<{
  gasOption: GasOptionEnum;
  homeUser: boolean;
}> = ({ gasOption, homeUser }) => {
  const [collaboratorName, setCollaboratorName] = React.useState<string>("");
  const { values } = useFormikContext<FormValues>();
  const {
    requestingCompany,
    premiseAddress: { state, postcode },
  } = values;
  const {
    context: { user },
  } = React.useContext(AppContext);

  React.useEffect(() => {
    const fetchCollaboratorName = async () => {
      if (!requestingCompany?.id || !values[gasOption]?.assignRequirements)
        return;

      try {
        const collaborators = await apiClient
          .get<Contact[]>(`/draft/collaborators/${requestingCompany.id}`)
          .then((response) => response.data);

        const collaboratorId = values[gasOption]?.collaboratorId;
        if (collaboratorId === user?.id) {
          setCollaboratorName(`${user.first_name} ${user.last_name}`);
        } else {
          const collaborator = collaborators.find(
            (c) => c.id === collaboratorId
          );
          setCollaboratorName(
            collaborator
              ? `${collaborator.first_name} ${collaborator.last_name}`
              : ""
          );
        }
      } catch (error) {
        console.error("Failed to fetch collaborators:", error);
      }
    };

    fetchCollaboratorName();
  }, [
    requestingCompany?.id,
    values[gasOption]?.assignRequirements,
    values[gasOption]?.collaboratorId,
    user,
  ]);

  const renderAccountResponsibility = () => (
    <ReadOnlyFieldWrapper value={values.requestingCompany?.name || homeUser}>
      <Columns
        collapseBelow="md"
        space={{ xs: "none", md: "xlarge" }}
        alignY="stretch"
      >
        <Column width="2/3">
          <Stack space="xsmall">
            <Stack space="xxsmall">
              <Text variant="body-small" tone="neutralLight">
                {`Who's responsible for the ${mapGasOptionToLabel(
                  state,
                  postcode
                )(gasOption)} ${
                  gasOption === GasOptionEnum.GAS_LINE ? "fee" : "account"
                }?`}
              </Text>
              <Text>
                {values[gasOption]?.accountPayer === AccountPayer.SOMEONE_ELSE
                  ? "Customer"
                  : values.requestingCompany?.name || "Me/My company"}
              </Text>
            </Stack>
            <Divider />
          </Stack>
        </Column>
        <Column width="1/3" />
      </Columns>
    </ReadOnlyFieldWrapper>
  );

  const renderAssignTo = () => (
    <ReadOnlyFieldWrapper value={values[gasOption]?.assignRequirements}>
      <Columns
        collapseBelow="md"
        space={{ xs: "none", md: "xlarge" }}
        alignY="stretch"
      >
        <Column width="2/3">
          <Stack space="xsmall">
            <Stack space="xxsmall">
              <Text variant="body-small" tone="neutralLight">
                Assigned to
              </Text>
              <Text>{collaboratorName}</Text>
            </Stack>
            <Divider />
          </Stack>
        </Column>
        <Column width="1/3" />
      </Columns>
    </ReadOnlyFieldWrapper>
  );

  return (
    <Stack space={{ xs: "xlarge", md: "xlarge" }}>
      {renderAccountResponsibility()}
      {renderAssignTo()}
    </Stack>
  );
};

export default ReadOnlyGasSectionInPremise;
